import { LINK_TYPE, isKnownLinkType } from '@/constants/banner-link-types';
import { openUrl } from '@/utils/browser-helper';
import { isMobileAppHost } from '@/utils/deeplink';

// Open a banner resource card's link the way its `linkType` asks. One destination
// field (`url`) carries the target; `linkType` decides how to treat it:
//   INTERNAL_PAGE  → an in-app router path, pushed onto the stack
//   IN_APP_BROWSER → opened in the Capacitor in-app browser (with tracking)
//   EXTERNAL_URL   → handed to the OS via window.open(url, '_system')
//
// An explicit `linkType` is obeyed as written — that is the author's override and
// nothing second-guesses it. Only an ABSENT (or unrecognised) type is inferred,
// and it is inferred as INTERNAL whenever the URL is something this app can route
// to. A bare path in a lesson note therefore stays in the app by default, which
// is what an author means by writing one.
//
// The inference reads the URL rather than defaulting blind, because the two blind
// defaults are both wrong in practice:
//   - default EXTERNAL (the old behaviour) sent `/library/sermon/x` to
//     UIApplication.shared.open, which silently does nothing. A dead tap.
//   - default INTERNAL would send youtube.com/shorts/… to router.push, which
//     matches no route (there is no catch-all) and also silently does nothing.
// Neither logged anything, so both look identical to "the button is broken".
//
// The EXTERNAL case is deliberately NOT routed through browser-helper's
// openExternal(): that helper allows only http/https/mailto/tel and targets
// '_blank', which cannot hand off custom schemes or universal links (youversion://,
// the Bible app, tel:, mailto:) to an installed app. '_system' lets the OS pick the
// handler, so external links keep working the way they always have.

export function isBarePath(url) {
  // A protocol-relative `//host/path` also starts with '/' and is NOT in-app.
  return url.startsWith('/') && !url.startsWith('//');
}

// The in-app router path for `url`, or null when there isn't one.
//
// `strict` is for the inference path only: it additionally requires an absolute
// URL to be on a host that serves THIS app (mobile.*), because those are the only
// hosts whose pathname is also a route here. `hogc.one/abc` and `hogc.link/nfccard`
// are short links — iOS reopens the app for them, but their pathname is not a
// router path, so deeplink.js resolves them instead.
export function toRouterPath(url, { strict = false } = {}) {
  if (!url) return null;
  if (isBarePath(url)) return url;
  try {
    const parsed = new URL(url);
    if (strict && !isMobileAppHost(parsed.hostname)) return null;
    // Non-hierarchical schemes parse happily but have no route in them:
    // `new URL('mailto:a@b.org').pathname` is 'a@b.org', and pushing that would
    // navigate to a garbage location. A real path always starts with '/'.
    if (!parsed.pathname.startsWith('/')) return null;
    if (parsed.pathname === '/') return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

// The effective LINK_TYPE for a block: the author's choice when they made one,
// otherwise inferred from the URL. Exported because analytics has to report the
// same answer the navigation acted on — ResourceCard's `is_external` flag used to
// be computed from a separate default and could disagree with what actually
// happened.
export function resolveLinkType(link) {
  if (isKnownLinkType(link?.linkType)) return link.linkType;
  return toRouterPath(link?.url, { strict: true }) ? LINK_TYPE.INTERNAL : LINK_TYPE.EXTERNAL;
}

// @param {{ linkType?: string, url?: string }} link
// @param {{ router?: object, metadata?: object }} deps
// @returns {Promise|void}
export function resolveBannerLink(link, { router, metadata = {} } = {}) {
  const url = link?.url;
  if (!url) return;

  const type = resolveLinkType(link);

  if (type === LINK_TYPE.INTERNAL) {
    // Not strict: an explicit INTERNAL_PAGE is the author's override, so any
    // absolute URL contributes its pathname rather than being refused.
    const path = toRouterPath(url);
    if (!path) {
      // Loud on purpose. This is the case that used to push an unmatched
      // location and leave the member staring at an unchanged screen with
      // nothing in the logs. console.error is forwarded to Datadog, so a
      // mis-authored banner is now visible without a device attached.
      console.error('[banner-link] INTERNAL_PAGE link has no in-app path, ignoring tap:', url);
      return;
    }
    if (router) router.push(path);
    return;
  }
  if (type === LINK_TYPE.IN_APP_BROWSER) {
    return openUrl({ url }, metadata);
  }
  window.open(url, '_system');
}
